import { ITaskBlock } from '../blocks-core/ITaskBlock'
import { BlockEngine } from '../blocks-core/BlockEngine'
import { StateStore } from './StateStore'
import { PythonRuntime } from './PythonRuntime'
import { Logger } from './Logger'
import React from 'react'
import { BlockConfigWithChildren, InterfaceConfig, PropertyBinding } from './types'
import { JSONObject } from '../../types/common.types'
import { JSONPath } from 'jsonpath-plus'
import { nestBlockConfigs } from '../utils/block-config.utils'

export class BlockComposer {
    private blockEngine: BlockEngine
    private stateStore: StateStore
    private pythonRuntime: PythonRuntime
    private inputData?: JSONObject

    constructor(
        blockEngine: BlockEngine,
        stateStore: StateStore,
        pythonRuntime: PythonRuntime,
        inputData?: JSONObject
    ) {
        this.blockEngine = blockEngine
        this.stateStore = stateStore
        this.pythonRuntime = pythonRuntime
        this.inputData = inputData
    }

    private resolveStateBindings(
        props: Record<string, unknown>,
        bindings?: Record<string, PropertyBinding>
    ): Record<string, unknown> {
        if (!bindings || !this.stateStore) return props

        const resolvedProps = { ...props }
        const logger = Logger.getInstance()

        Object.entries(bindings).forEach(([propName, binding]) => {
            try {
                if (binding.type === 'stateBinding') {
                    const signal = this.stateStore.getSignal(binding.value)
                    if (signal) {
                        resolvedProps[propName] = signal
                    }
                }
                if (binding.type === 'dataBinding') {
                    if (this.inputData) {
                        const content = JSONPath({
                            path: binding.value,
                            json: this.inputData,
                            wrap: false,
                        })
                        resolvedProps[propName] = content
                    }
                }
            } catch (error) {
                logger.error(`Failed to resolve binding for property "${propName}"`, {
                    source: 'BlockComposer',
                    error: error instanceof Error ? error : String(error),
                    context: {
                        propName,
                        bindingType: binding.type,
                        bindingValue: binding.value,
                        hint: 'The binding expression may be invalid. Check your data binding JSONPath or state variable name.',
                    },
                })
            }
        })

        return resolvedProps
    }

    private createEventHandlers(blockConfigId: string, blockConfigName: string) {
        const eventHandlers: Record<
            string,
            (
                event: React.SyntheticEvent,
                blockId: string,
                blockName: string,
                itemIndex?: number
            ) => void
        > = {}

        if (this.pythonRuntime) {
            const registeredEvents = this.pythonRuntime.getEventTypes(
                blockConfigId,
                blockConfigName
            )

            registeredEvents.forEach(eventType => {
                eventHandlers[eventType] = (
                    eventData: React.SyntheticEvent<Element, Event>,
                    blockId: string,
                    blockName: string,
                    itemIndex?: number
                ) => {
                    this.pythonRuntime!.dispatchEvent({
                        blockConfigId,
                        blockConfigName,
                        eventType,
                        eventData: {
                            value: eventData,
                            itemIndex: itemIndex,
                            blockId: blockId,
                            blockName: blockName,
                        },
                    })
                }
            })
        }

        return eventHandlers
    }

    private attachEventHandlersToBlock(
        block: ITaskBlock,
        eventHandlers: Record<
            string,
            (
                event: React.SyntheticEvent,
                blockId: string,
                blockName: string,
                itemIndex?: number
            ) => void
        >
    ): void {
        Object.entries(eventHandlers).forEach(([eventType, handler]) => {
            block.setListener(eventType, ((...args: unknown[]) => {
                handler(
                    args[0] as React.SyntheticEvent,
                    (args[1] as string | undefined) ?? block.id,
                    (args[2] as string | undefined) ?? block.name,
                    (args[3] as number | undefined) ?? undefined
                )
            }) as (...args: unknown[]) => unknown)
        })
    }

    private createBlockWithChildren(
        blockConfig: BlockConfigWithChildren,
        skipEventHandlers: boolean = false,
        isHiddenTemplate: boolean = false
    ): ITaskBlock {
        this.createChildBlocks(blockConfig, skipEventHandlers, isHiddenTemplate)
        const resolvedProps = this.resolveStateBindings(
            blockConfig.properties,
            blockConfig.bindings
        )
        const childBlockConfigs: BlockConfigWithChildren[] = blockConfig.children || []
        const childBlockConfigIds = childBlockConfigs.map(conf => conf.id)
        const block = this.blockEngine.addBlock(
            blockConfig.type,
            {
                id: blockConfig.id,
                name: blockConfig.name,
                componentProps: {
                    ...resolvedProps,
                    id: blockConfig.id,
                    childBlockConfigIds,
                    childBlockConfigs,
                },
            },
            isHiddenTemplate
        )

        if (!skipEventHandlers) {
            const eventHandlers = this.createEventHandlers(blockConfig.id, blockConfig.name)
            this.attachEventHandlersToBlock(block, eventHandlers)
        }

        return block
    }

    private createChildBlocks(
        blockConfig: BlockConfigWithChildren,
        skipEventHandlers: boolean = false,
        isHiddenTemplate: boolean = false
    ) {
        if (blockConfig.type === 'list') {
            const childConfigs: BlockConfigWithChildren[] = blockConfig.children || []
            childConfigs.forEach(childConfig => {
                this.createBlockWithChildren(childConfig, skipEventHandlers, true)
            })
            return
        }
        const childConfigs: BlockConfigWithChildren[] = blockConfig.children || []
        childConfigs.forEach(childConfig => {
            this.createBlockWithChildren(childConfig, skipEventHandlers, isHiddenTemplate)
        })
    }

    compose(config: InterfaceConfig, skipEventHandlers: boolean = false): ITaskBlock[] {
        const nestedConfigs = nestBlockConfigs(config.blockConfigs)

        const rootBlocks = nestedConfigs
            .map(blockConfig => this.createBlockWithChildren(blockConfig, skipEventHandlers))
            .filter(Boolean) as ITaskBlock[]

        return rootBlocks
    }

    updateEventHandlers(): void {
        const blocks = this.blockEngine.getAllBlocks()
        blocks.forEach(block => {
            const eventHandlers = this.createEventHandlers(block.id, block.name)
            this.attachEventHandlersToBlock(block, eventHandlers)
        })

        const hiddenBlocks = this.blockEngine.getAllHiddenBlocks()
        hiddenBlocks.forEach(block => {
            const eventHandlers = this.createEventHandlers(block.id, block.name)
            this.attachEventHandlersToBlock(block, eventHandlers)
        })
    }
}
